import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sparkles, Image, Upload, Trash2, Loader2, Calendar, ToggleLeft, ToggleRight, Plus, X } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMarketing = () => {
  const { getAuthHeader } = useAuth();
  
  // Overlay state
  const [overlayEnabled, setOverlayEnabled] = useState(false);
  const [overlayImage, setOverlayImage] = useState(null);
  const [uploadingOverlay, setUploadingOverlay] = useState(false);
  
  // Global Marketing Images (permanent)
  const [marketingImages, setMarketingImages] = useState([]);
  const [newMarketingImage, setNewMarketingImage] = useState({ name: '', position: 1 });
  const [uploadingMarketing, setUploadingMarketing] = useState(false);
  
  // Promotional Images (with end dates)
  const [promoImages, setPromoImages] = useState([]);
  const [newPromoImage, setNewPromoImage] = useState({ name: '', position: 1, end_date: '' });
  const [uploadingPromo, setUploadingPromo] = useState(false);
  
  const overlayInputRef = useRef(null);
  const marketingInputRef = useRef(null);
  const promoInputRef = useRef(null);

  useEffect(() => {
    fetchOverlaySettings();
    fetchMarketingImages();
    fetchPromoImages();
  }, []);

  // ===== Overlay Functions =====
  const fetchOverlaySettings = async () => {
    try {
      const response = await axios.get(`${API}/marketing/overlay`);
      setOverlayEnabled(response.data.enabled);
      setOverlayImage(response.data.image_url);
    } catch (error) {
      console.error('Error fetching overlay settings:', error);
    }
  };

  const handleOverlayToggle = async (enabled) => {
    try {
      await axios.put(`${API}/marketing/overlay?enabled=${enabled}&image_url=${overlayImage || ''}`, {}, {
        headers: getAuthHeader(),
      });
      setOverlayEnabled(enabled);
      toast.success(enabled ? 'Overlay activé' : 'Overlay désactivé');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleOverlayUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingOverlay(true);
    const formData = new FormData();
    formData.append('files', file);

    try {
      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.uploaded.length > 0) {
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.uploaded[0].url}`;
        await axios.put(`${API}/marketing/overlay?enabled=${overlayEnabled}&image_url=${encodeURIComponent(imageUrl)}`, {}, {
          headers: getAuthHeader(),
        });
        setOverlayImage(imageUrl);
        toast.success('Image overlay téléversée!');
      }
    } catch (error) {
      toast.error('Erreur lors du téléversement');
    } finally {
      setUploadingOverlay(false);
      if (overlayInputRef.current) overlayInputRef.current.value = '';
    }
  };

  // ===== Marketing Images Functions =====
  const fetchMarketingImages = async () => {
    try {
      const response = await axios.get(`${API}/marketing/global-images`);
      setMarketingImages(response.data);
    } catch (error) {
      console.error('Error fetching marketing images:', error);
    }
  };

  const handleMarketingUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !newMarketingImage.name) {
      toast.error('Veuillez entrer un nom pour l\'image');
      return;
    }

    setUploadingMarketing(true);
    const formData = new FormData();
    formData.append('files', file);

    try {
      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.uploaded.length > 0) {
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.uploaded[0].url}`;
        await axios.post(`${API}/marketing/global-images`, {
          name: newMarketingImage.name,
          image_url: imageUrl,
          position: newMarketingImage.position,
        }, { headers: getAuthHeader() });

        setNewMarketingImage({ name: '', position: 1 });
        fetchMarketingImages();
        toast.success('Image marketing ajoutée!');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setUploadingMarketing(false);
      if (marketingInputRef.current) marketingInputRef.current.value = '';
    }
  };

  const deleteMarketingImage = async (id) => {
    try {
      await axios.delete(`${API}/marketing/global-images/${id}`, { headers: getAuthHeader() });
      fetchMarketingImages();
      toast.success('Image supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const updateMarketingPosition = async (id, position) => {
    try {
      await axios.put(`${API}/marketing/global-images/${id}?position=${position}`, {}, { headers: getAuthHeader() });
      fetchMarketingImages();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // ===== Promo Images Functions =====
  const fetchPromoImages = async () => {
    try {
      const response = await axios.get(`${API}/marketing/promo-images?include_expired=true`, {
        headers: getAuthHeader(),
      });
      setPromoImages(response.data);
    } catch (error) {
      console.error('Error fetching promo images:', error);
    }
  };

  const handlePromoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !newPromoImage.name || !newPromoImage.end_date) {
      toast.error('Veuillez remplir le nom et la date de fin');
      return;
    }

    setUploadingPromo(true);
    const formData = new FormData();
    formData.append('files', file);

    try {
      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.uploaded.length > 0) {
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.uploaded[0].url}`;
        await axios.post(`${API}/marketing/promo-images`, {
          name: newPromoImage.name,
          image_url: imageUrl,
          position: newPromoImage.position,
          end_date: newPromoImage.end_date,
        }, { headers: getAuthHeader() });

        setNewPromoImage({ name: '', position: 1, end_date: '' });
        fetchPromoImages();
        toast.success('Image promotionnelle ajoutée!');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setUploadingPromo(false);
      if (promoInputRef.current) promoInputRef.current.value = '';
    }
  };

  const deletePromoImage = async (id) => {
    try {
      await axios.delete(`${API}/marketing/promo-images/${id}`, { headers: getAuthHeader() });
      fetchPromoImages();
      toast.success('Image supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const isExpired = (endDate) => new Date(endDate) < new Date();

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-marketing">
        {/* Header */}
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Marketing & Promotions
          </h1>
          <p className="text-slate-500 text-xs">Gérer les overlays et images promotionnelles</p>
        </div>

        {/* Section 1: Overlay/Watermark */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Image className="h-4 w-4 text-purple-600" />
                1. Overlay / Filigrane
              </h2>
              <p className="text-slate-500 text-[10px]">Image superposée sur toutes les photos de véhicules</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500">{overlayEnabled ? 'Activé' : 'Désactivé'}</span>
              <Switch
                checked={overlayEnabled}
                onCheckedChange={handleOverlayToggle}
                disabled={!overlayImage}
              />
            </div>
          </div>

          <div className="flex items-start gap-4">
            {/* Preview */}
            <div className="w-40 h-28 bg-white rounded-lg overflow-hidden flex items-center justify-center border-2 border-dashed border-purple-200">
              {overlayImage ? (
                <img src={overlayImage} alt="Overlay" className="w-full h-full object-contain" />
              ) : (
                <span className="text-slate-400 text-[10px] text-center px-3">Aucun overlay</span>
              )}
            </div>

            {/* Upload */}
            <div className="flex-1">
              <input
                ref={overlayInputRef}
                type="file"
                accept="image/png,image/webp"
                onChange={handleOverlayUpload}
                className="hidden"
              />
              <Button
                onClick={() => overlayInputRef.current?.click()}
                disabled={uploadingOverlay}
                className="bg-purple-600 hover:bg-purple-700 text-xs h-8"
                size="sm"
              >
                {uploadingOverlay ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                Téléverser
              </Button>
              <p className="text-[10px] text-slate-500 mt-1.5">
                PNG ou WebP avec transparence recommandé
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Marketing Images (Permanent) */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="mb-3">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Image className="h-4 w-4 text-blue-600" />
              2. Images Marketing (Permanentes)
            </h2>
            <p className="text-slate-500 text-[10px]">Ces images apparaissent dans le carrousel de TOUS les véhicules</p>
          </div>

          {/* Add new marketing image */}
          <div className="bg-slate-50 rounded-lg p-3 mb-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-600 text-[10px]">Nom de l'image</Label>
                <Input
                  value={newMarketingImage.name}
                  onChange={(e) => setNewMarketingImage(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Bannière financement"
                  className="bg-white border-slate-200 mt-1 text-xs h-7"
                />
              </div>
              <div>
                <Label className="text-slate-600 text-[10px]">Position</Label>
                <Input
                  type="number"
                  value={newMarketingImage.position}
                  onChange={(e) => setNewMarketingImage(prev => ({ ...prev, position: parseInt(e.target.value) || 1 }))}
                  min="1"
                  className="bg-white border-slate-200 mt-1 text-xs h-7"
                />
              </div>
              <div className="flex items-end">
                <input
                  ref={marketingInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleMarketingUpload}
                  className="hidden"
                />
                <Button
                  onClick={() => marketingInputRef.current?.click()}
                  disabled={uploadingMarketing || !newMarketingImage.name}
                  className="bg-blue-600 hover:bg-blue-700 w-full text-xs h-7"
                  size="sm"
                >
                  {uploadingMarketing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                  Ajouter
                </Button>
              </div>
            </div>
          </div>

          {/* List of marketing images */}
          {marketingImages.length === 0 ? (
            <p className="text-slate-400 text-center py-4 text-xs">Aucune image marketing configurée</p>
          ) : (
            <div className="space-y-2">
              {marketingImages.map((img) => (
                <div key={img.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2">
                  <img src={img.image_url} alt={img.name} className="w-20 h-14 object-cover rounded" />
                  <div className="flex-1">
                    <p className="text-slate-800 font-medium text-xs">{img.name}</p>
                    <p className="text-slate-500 text-[10px]">Position: {img.position}</p>
                  </div>
                  <Input
                    type="number"
                    value={img.position}
                    onChange={(e) => updateMarketingPosition(img.id, parseInt(e.target.value) || 1)}
                    min="1"
                    className="w-16 bg-white border-slate-200 text-xs h-7"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteMarketingImage(img.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 3: Promotional Images (With End Dates) */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="mb-3">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-600" />
              3. Images Promotionnelles (Avec date de fin)
            </h2>
            <p className="text-slate-500 text-[10px]">Ces images apparaissent jusqu'à la date de fin</p>
          </div>

          {/* Add new promo image */}
          <div className="bg-slate-50 rounded-lg p-3 mb-3">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-slate-600 text-[10px]">Nom</Label>
                <Input
                  value={newPromoImage.name}
                  onChange={(e) => setNewPromoImage(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Soldes janvier"
                  className="bg-white border-slate-200 mt-1 text-xs h-7"
                />
              </div>
              <div>
                <Label className="text-slate-600 text-[10px]">Position</Label>
                <Input
                  type="number"
                  value={newPromoImage.position}
                  onChange={(e) => setNewPromoImage(prev => ({ ...prev, position: parseInt(e.target.value) || 1 }))}
                  min="1"
                  className="bg-white border-slate-200 mt-1 text-xs h-7"
                />
              </div>
              <div>
                <Label className="text-slate-600 text-[10px]">Date de fin</Label>
                <Input
                  type="date"
                  value={newPromoImage.end_date}
                  onChange={(e) => setNewPromoImage(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-white border-slate-200 mt-1 text-xs h-7"
                />
              </div>
              <div className="flex items-end">
                <input
                  ref={promoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePromoUpload}
                  className="hidden"
                />
                <Button
                  onClick={() => promoInputRef.current?.click()}
                  disabled={uploadingPromo || !newPromoImage.name || !newPromoImage.end_date}
                  className="bg-amber-600 hover:bg-amber-700 w-full text-xs h-7"
                  size="sm"
                >
                  {uploadingPromo ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                  Ajouter
                </Button>
              </div>
            </div>
          </div>

          {/* List of promo images */}
          {promoImages.length === 0 ? (
            <p className="text-slate-400 text-center py-4 text-xs">Aucune image promotionnelle configurée</p>
          ) : (
            <div className="space-y-2">
              {promoImages.map((img) => (
                <div 
                  key={img.id} 
                  className={`flex items-center gap-3 rounded-lg p-2 ${
                    isExpired(img.end_date) ? 'bg-red-50 border border-red-200' : 'bg-slate-50'
                  }`}
                >
                  <img src={img.image_url} alt={img.name} className="w-20 h-14 object-cover rounded" />
                  <div className="flex-1">
                    <p className="text-slate-800 font-medium text-xs">{img.name}</p>
                    <p className="text-slate-500 text-[10px]">
                      Position: {img.position} • Expire: {new Date(img.end_date).toLocaleDateString('fr-CA')}
                    </p>
                  </div>
                  {isExpired(img.end_date) && (
                    <span className="text-[9px] bg-red-600 text-white px-1.5 py-0.5 rounded">EXPIRÉ</span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deletePromoImage(img.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMarketing;
